import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  isWithinInterval,
  parse,
  startOfMonth,
  startOfToday,
} from "date-fns";
import React from "react";
import {
  changeDateTimeFromUTCStringToSchoolTimeZone,
  createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime,
} from "../../Calendar/calendarFunctions";
import AddNote from "../../CreateTimesheet/AddNote/AddNote";
import CreateTimesheetAreYouSure from "../../CreateTimesheet/CreateTimesheetAreYouSure/CreateTimesheetAreYouSure";
import { baseUrl } from "../../assets/Animations";
import AutoGeneratedFamilyCard from "./AutoGeneratedFamilyCard/AutoGeneratedFamilyCard";
import "./auto-populate-timesheet.css";

const AutoPopulatedTimesheet = (props) => {
  const [families, setFamilies] = React.useState([]);
  const [finalWarning, setFinalWarning] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [notesOpen, setNotesOpen] = React.useState(false);
  const [estimatedPay, setEstimatedPay] = React.useState(0);
  const [incomplete, setIncomplete] = React.useState(false);
  const [monthNumber, setMonthNumber] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [calendarEvents, setCalendarEvents] = React.useState([]);
  const [calendarDaysWithEvents, setCalendarDaysWithEvents] = React.useState(
    {}
  );
  const [cancellationsWeNeedToChargeFor, setCancellationsWeNeedToChargeFor] =
    React.useState([]);
  const [currentMonth, setCurrentMonth] = React.useState(
    format(startOfToday(), "MMMM yyyy")
  );
  const [daysOfTheDisplayedMonth, setDaysOfTheDisplayedMonth] = React.useState(
    eachDayOfInterval({
      start: startOfMonth(parse(currentMonth, "MMMM yyyy", new Date())),
      end: endOfMonth(parse(currentMonth, "MMMM yyyy", new Date())),
    })
  );
  const [familiesEventsObject, setFamiliesEventsObject] = React.useState({});

  const myToken = localStorage.getItem("token");
  const myHeader = { headers: { Authorization: `Bearer ${myToken}` } };

  React.useEffect(() => {
    calculateEstimatedPay();
    getTeachersCalendarEvents();
    setCurrentMonth(correctMonthAndYearForTimesheetDate());
    setMonthNumber(
      format(
        parse(correctMonthAndYearForTimesheetDate(), "MMMM yyyy", new Date()),
        "M"
      )
    );
  }, []);

  React.useEffect(() => {
    calculateEstimatedPay();

    //CHECK IF ALL EXTRAS ARE FILLED OUT
    for (let i = 0; i < families.length; i++) {
      let theFamily = families[i];
      for (let i = 0; i < theFamily.extras.length; i++) {
        if (
          theFamily.extras[i].description === "" ||
          theFamily.extras[i].amount === 0
        ) {
          setIncomplete(true);
          return;
        } else {
          setIncomplete(false);
        }
      }
    }
  }, [families]);

  React.useEffect(() => {
    organizeTheTimesheet();
  }, [calendarDaysWithEvents]);

  const organizeTheTimesheet = () => {
    let multiFamilyObject = {};
    let totalMinutesTaught = 0;
    Object.keys(calendarDaysWithEvents).map((thing) => {
      for (let j = 0; j < calendarDaysWithEvents[thing].events.length; j++) {
        if (!calendarDaysWithEvents[thing].events[j].isPendingOffer) {
          totalMinutesTaught =
            totalMinutesTaught +
            calendarDaysWithEvents[thing].events[j].lessonLength;
        }
        if (
          multiFamilyObject[calendarDaysWithEvents[thing].events[j].familyName]
        ) {
          if (!calendarDaysWithEvents[thing].events[j].isPendingOffer) {
            multiFamilyObject[
              calendarDaysWithEvents[thing].events[j].familyName
            ].push(calendarDaysWithEvents[thing].events[j]);
          }
        } else {
          if (!calendarDaysWithEvents[thing].events[j].isPendingOffer) {
            multiFamilyObject[
              calendarDaysWithEvents[thing].events[j].familyName
            ] = [calendarDaysWithEvents[thing].events[j]];
          }
        }
      }
    });

    //INSERT ANY CANCELLATIONS IN MULTI FAMILY OBJECT
    for (let i = 0; i < cancellationsWeNeedToChargeFor.length; i++) {
      if (multiFamilyObject[cancellationsWeNeedToChargeFor[i].familyName]) {
        multiFamilyObject[cancellationsWeNeedToChargeFor[i].familyName].push(
          cancellationsWeNeedToChargeFor[i]
        );
      }
    }

    setEstimatedPay((totalMinutesTaught / 60) * Number(props.rate));
    setFamiliesEventsObject(multiFamilyObject);
    createEachFamilyObject(multiFamilyObject);
  };

  const correctMonthAndYearForTimesheetDate = () => {
    let d = new Date();
    let month = d.getMonth();
    let day = d.getDate();
    let theYear = d.getFullYear();
    let adjustedMonth;
    if (day < 15) {
      if (month !== 0) {
        adjustedMonth = month;
      } else if (month === 0) {
        theYear = theYear - 1;
        adjustedMonth = 12;
      } else {
        adjustedMonth = month + 1;
      }
    } else {
      adjustedMonth = month + 1;
    }
    return format(new Date(theYear, adjustedMonth - 1, 1), "MMMM yyyy");
  };

  //CHECK IF ANY OF THE CANCELLATIONS ARE LESS THAN 24 HOURS AND SHOULD BE PAID
  const checkForCancellationsLessThan24Hours = (events) => {
    const targetMonthStart = startOfMonth(
      parse(correctMonthAndYearForTimesheetDate(), "MMMM yyyy", new Date())
    );
    const targetMonthEnd = endOfMonth(
      parse(correctMonthAndYearForTimesheetDate(), "MMMM yyyy", new Date())
    );

    // Filter events that fall within the target month
    const eventsInTargetMonth = events.filter((event) => {
      let timezoneEvent = changeDateTimeFromUTCStringToSchoolTimeZone(event);
      const eventDate = new Date(timezoneEvent.dateTime);
      if (
        isWithinInterval(eventDate, {
          start: targetMonthStart,
          end: targetMonthEnd,
        }) &&
        event.lessThan24Hours &&
        event.chargedTheFamily
      ) {
        return event;
      }
    });

    return eventsInTargetMonth;
  };

  const createEachFamilyObject = async (multiFamilyObject) => {
    Object.keys(multiFamilyObject).map(async (family) => {
      let famResponse = await axios.get(
        `${baseUrl}/school-management/${multiFamilyObject[family][0].familyId}`,
        myHeader
      );
      let familyObject = {
        familyName: family,
        familyWaveId: famResponse.data.waveCustomerId,
        email: famResponse.data.primaryEmail,
        school: famResponse.data.school,
        listOfFamilyStudents: [],
        id: famResponse.data.familyId,
        cancellations: [],
        lessons: [],
        extras: [],
      };
      for (let i = 0; i < multiFamilyObject[family].length; i++) {
        familyObject.lessons.push({
          id: multiFamilyObject[family][i].lessonId,
          studentName: multiFamilyObject[family][i].studentName,
          lessonLength: multiFamilyObject[family][i].lessonLength,
          date: format(new Date(multiFamilyObject[family][i].dateTime), "d"),
          studentRate: returnStudentRate(
            multiFamilyObject[family][i].studentId,
            famResponse.data
          ),
          studentAllRates: returnAllLessonRates(
            multiFamilyObject[family][i].studentId,
            famResponse.data
          ),
        });
      }
      setFamilies((prev) => [...prev, familyObject]);
    });
  };

  const returnStudentRate = (studentId, familyInfo) => {
    let student = familyInfo.students.find(
      (student) => student.studentId === studentId
    );
    return student.lessonRate;
  };

  const returnAllLessonRates = (studentId, familyInfo) => {
    let student = familyInfo.students.find(
      (student) => student.studentId === studentId
    );
    return student.allRates;
  };

  const addAnExtra = (emailAsId) => {
    let newFamilies = [...families];
    let theFamily = newFamilies.find((family) => family.email === emailAsId);
    theFamily.extras.push({
      description: "",
      amount: 0,
    });
    setFamilies(newFamilies);
  };

  const changeExtraDescription = (emailAsId, index, value) => {
    let newFamilies = [...families];
    let theFamily = newFamilies.find((family) => family.email === emailAsId);
    theFamily.extras[index].description = value;
    setFamilies(newFamilies);
  };

  const changeExtraAmount = (emailAsId, index, value) => {
    let newFamilies = [...families];
    let theFamily = newFamilies.find((family) => family.email === emailAsId);
    theFamily.extras[index].amount = value;
    setFamilies(newFamilies);
  };

  const removeExtra = (emailAsId, index) => {
    let newFamilies = [...families];
    let theFamily = newFamilies.find((family) => family.email === emailAsId);
    theFamily.extras.splice(index, 1);
    setFamilies(newFamilies);
  };

  const getTeachersCalendarEvents = () => {
    let eachDayOfTimesheetMonth = eachDayOfInterval({
      start: startOfMonth(
        parse(correctMonthAndYearForTimesheetDate(), "MMMM yyyy", new Date())
      ),
      end: endOfMonth(
        parse(correctMonthAndYearForTimesheetDate(), "MMMM yyyy", new Date())
      ),
    });

    Promise.all([fetchTeachersCalendarEvents(), fetchTeachersCancellations()])
      .then(([eventResponse, cancellationResponse]) => {
        setCalendarEvents(eventResponse.data);
        setTimeout(() => {
          createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
            eachDayOfTimesheetMonth,
            eventResponse.data,
            setCalendarDaysWithEvents
          );
          setLoading(false);
        }, 2000);
        setCancellationsWeNeedToChargeFor(
          checkForCancellationsLessThan24Hours(cancellationResponse.data)
        );
      })
      .catch((err) => console.log(err));
  };

  const fetchTeachersCalendarEvents = async () =>
    axios.get(
      `${baseUrl}/calendar/teacher/${props.userData.TeacherID}`,
      myHeader
    );
  const fetchTeachersCancellations = async () =>
    axios.get(
      `${baseUrl}/cancellation/teacher/${props.userData.TeacherID}`,
      myHeader
    );

  const calculateEstimatedPay = () => {
    let minutesTaught = 0;
    let allExtras = 0;
    for (let i = 0; i < families.length; i++) {
      let fam = families[i];
      for (let i = 0; i < fam.lessons.length; i++) {
        minutesTaught =
          Number(minutesTaught) + Number(fam.lessons[i].lessonLength);
      }
      for (let i = 0; i < fam.cancellations.length; i++) {
        if (fam.cancellations[i].cancellor === "student less than 24hrs") {
          minutesTaught =
            Number(minutesTaught) + Number(fam.cancellations[i].lessonLength);
        }
      }
      for (let i = 0; i < fam.extras.length; i++) {
        allExtras = Number(allExtras) + Number(fam.extras[i].amount);
      }
    }
    setEstimatedPay((minutesTaught / 60) * Number(props.rate) + allExtras);
  };

  const tryToSubmitTimesheet = () => {
    setFinalWarning(true);
  };

  const submitTimesheet = async () => {
    let teachersTime = {
      timesheetId: Math.random().toString(16).slice(2),
      teacherId: props.userData.TeacherID,
      teacherName: props.userData.Name,
      families: families,
      status: "submitted",
      note: note,
      submitDate: new Date(),
      monthYear: `${props.month}/${props.myYear}`,
      teacherRate: props.rate,
      school: props.userData.School,
      email: props.userData.Email,
      monthNumber: monthNumber,
      year: props.myYear,
    };
    setFinalWarning(false);
    axios
      .post(
        `${baseUrl}/teachertimesheet`,
        {
          teacherTimesheet: teachersTime,
        },
        myHeader
      )
      .then((res) => {
        props.pushNewTimesheetToTimesheets(res.data);
        props.setOpenUpTheTimesheet(false);
        props.setThankYouForSubmitting(true);
        props.setTryingToSubmitTimesheet(false);
      })
      .catch((err) => {
        console.log(err);
        console.log("oops there was an error");
      });
  };

  const cancelOutOfTimesheetProcess = () => {
    props.setTryingToSubmitTimesheet(false);
    props.setOpenUpTheTimesheet(false);
  };

  return (
    <div className="create-autogenerated-timesheet">
      {finalWarning && (
        <CreateTimesheetAreYouSure
          submitTimesheet={submitTimesheet}
          setFinalWarning={setFinalWarning}
        />
      )}
      {notesOpen && (
        <AddNote setNotesOpen={setNotesOpen} note={note} setNote={setNote} />
      )}
      <div className="another-x-wrapper">
        <ArrowLeftIcon
          color="#BCC2C7"
          className="auto-timesheet-back-arrow"
          onClick={() => cancelOutOfTimesheetProcess()}
        />
      </div>
      <h1 className="auto-timesheet-title">Timesheet</h1>
      <h2 className="auto-timesheet-month-year">
        {props.month} {props.myYear}
      </h2>
      {loading ? (
        <div className="auto-timesheet-loading-wrapper">
          <div>Generating Timesheet...</div>
        </div>
      ) : (
        <div>
          <div className="family-card-section">
            {families.map((family, index) => (
              <AutoGeneratedFamilyCard
                key={index}
                family={family}
                monthNumber={monthNumber}
                addAnExtra={addAnExtra}
                changeExtraDescription={changeExtraDescription}
                changeExtraAmount={changeExtraAmount}
                removeExtra={removeExtra}
              />
            ))}
          </div>
          <div className="create-auto-generated-timesheet-bottom-sections">
            <h3 className="create-auto-generated-timesheet__estimated-pay">
              Estimate Pay: ${estimatedPay}
            </h3>
            <div className="create-auto-generated-timesheet__notes-area-wrapper">
              {note === "" && (
                <div
                  className="create-auto-generated-timesheet__add-note"
                  onClick={() => setNotesOpen(true)}
                >
                  + Add a note
                </div>
              )}
              {note !== "" && (
                <div>
                  <div className="create-auto-generated-timesheet_notes-title-area">
                    <div className="create-auto-generated-timesheet__notes-title">
                      Notes
                    </div>
                    <PencilSquareIcon
                      className="generate-auto-timesheet-pencil"
                      color="gray"
                      width={"30px"}
                      onClick={() => setNotesOpen(true)}
                    />
                  </div>
                  <div className="create-auto-generated-timesheet__actual-note">
                    {note}
                  </div>
                </div>
              )}
            </div>
            <div>
              <div
                className="create-auto-generated-timesheet__submit-timesheet"
                onClick={() => tryToSubmitTimesheet()}
                id="create-auto-generated-timesheet__submit-btn"
              >
                Submit Timesheet
              </div>
              {incomplete && (
                <p style={{ color: "red" }}>Please Fill Out Everything</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AutoPopulatedTimesheet;
